import { Category } from 'src/Elements/Sidebar/Categories'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { Box, Divider, IconButton, SwipeableDrawer } from '@mui/material'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import { makeStyles } from 'tss-react/mui'
import UserBadge from '../UserBadge'
import SidebarLinks from '../Navigation/SidebarLinks'
import LanguageSelector from '../User/UserSettings/LanguageSelector'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import LogoutButton from '@e3dc-react/shell/Elements/Buttons/Settings/LogoutButton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import E3dcLinks from '../Framework/E3dcLinks'
import { J2Multiply } from '@e3dc-react/icons'
import { usePreventReloadOnWebview } from 'src/Libs/navigation/hooks/use-prevent-reload-on-webview'
import { isFlowTheme } from 'src/Libs/utils/theme-helpers'

const HEADER_HEIGHT_WITH_PADDING = 112

const useStyles = makeStyles()(theme => ({
  moreContainer: {
    width: '100%',
    flexDirection: 'column',
    position: 'absolute',
    flexWrap: 'wrap',
    background: theme.palette.background.sidebar,
    zIndex: theme.zIndex.drawer + 1,
    height: '100dvh', // was 100vh
    maxHeight: '100dvh', // was 100vh
    top: 0,
    borderRadius: 0,
    padding: 0,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 10000,
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.sidebar,
    },
  },
  dragHandleWrapper: {
    position: 'absolute',
    top: theme.spacing(2),
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    left: 0,
  },
  dragHandle: {
    height: 5,
    borderRadius: 2.5,
    width: 80,
    marginTop: `-${theme.spacing(1)}`,
    background: theme.palette.text.header,
    opacity: 0.6,
  },
  profileContainer: {
    position: 'relative',
    height: 100,
    width: '100%',
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    background: theme.palette.background.sidebarProfile,
    borderRadius: `0 0 ${theme.shape.header.borderRadius} ${theme.shape.header.borderRadius}`,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100dvh - ${HEADER_HEIGHT_WITH_PADDING}px)`,
    maxHeight: `calc(100dvh - ${HEADER_HEIGHT_WITH_PADDING}px)`,
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  divider: {
    display: 'flex',
    flexDirection: 'column',
    borderColor: theme.palette.grey.grey04,
    marginLeft: `-${theme.spacing(2)}`,
    marginRight: `-${theme.spacing(2)}`,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  logoutButton: {
    '& *': {
      color: theme.palette.text.sidebar,
    },
    height: theme.spacing(4),
    gap: theme.spacing(1),
    width: '100%', // stretch over the whole width
    justifyContent: 'flex-start', // keep text aligned to the left
    display: 'flex', // ensure flex layout
  },
  links: {
    overflowY: 'auto',
  },
}))

interface Props {
  links: Category[]
  onClose?: () => void
}

const MoreBottomActions: React.VFC<Props> = ({ links, onClose }) => {
  const navigate = useNavigate()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const moreActive = useTypedSelector(state => state.app.moreActive)
  const [language, setLanguage] = useState<AppLang>(getCurrentLanguage())
  usePreventReloadOnWebview(moreActive)

  return (
    <SwipeableDrawer
      open={moreActive}
      onClose={() => dispatch(appUpdate({ moreActive: false }))}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onOpen={() => {}}
      anchor={'bottom'}
      classes={{
        paper: classes.moreContainer,
      }}
    >
      <IconButton onClick={() => dispatch(appUpdate({ moreActive: false }))} className={classes.closeButton}>
        <J2Multiply />
      </IconButton>
      <Box className={classes.profileContainer}>
        <Box className={classes.dragHandleWrapper}>
          <Box className={classes.dragHandle} />
        </Box>
        <UserBadge onUserProfileClick={() => navigate('/user')} hasMenu={false} hasBackground={false} />
      </Box>
      <Box className={classes.content}>
        <Box className={classes.links}>
          <SidebarLinks links={links} onClose={onClose} />
        </Box>
        <Box>
          <Divider className={classes.divider} />
          <Box className={classes.actions}>
            <LanguageSelector language={language} setLanguage={setLanguage} needsChangeConfirmation={false} />
            <LogoutButton
              handleLogout={() => {
                /* The dispatch and timeout is needed for the react native app, otherwise logging out is not working correctly */
                dispatch(appUpdate({ moreActive: false }))
                setTimeout(() => navigate('/logout'), 500)
              }}
              text={t('common.logout')}
              buttonClass={classes.logoutButton}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>{!isFlowTheme && <E3dcLinks />}</Box>
          </Box>
        </Box>
      </Box>
    </SwipeableDrawer>
  )
}

export default MoreBottomActions
